// The editor's asset library (Stickers, Backgrounds): register files in it.
// Shared by the PSD importer and the admin toolbar's "Make sticker", which must
// not import the importer (it pulls in ag-psd).
//
// NO DUPLICATES ACROSS PCs. Every library upload carries a `content_key`: a
// hash of what the image IS, computed the same way on every machine
// ("psd1:..." from a Photoshop layer's decoded pixels, "img1:..." from an
// image's downloaded bytes). Before uploading, the server is asked for an asset
// with that key; if one exists it is reused. The key has its own backend field
// because the AI metadata step overwrites `name` and `keywords` right after
// create.

import { apiMultiPartPost, apiPost } from "./apiCall";
import { ENDPOINTS } from "../constants/apiurl";
import { EDITOR_ASSETS } from "../constants";

export const pickUrls = (urls = []) => {
  const large = urls.find((u) => u.size === "large") || urls[0];
  return { url: large?.url || null, urls };
};

// Redux userDetails is the source of truth (the localStorage copy is wiped on
// beforeunload); the cart's brand is the fallback.
export const brandIdOf = (projectSetup) =>
  projectSetup?.userDetails?.brand_id || projectSetup?.cartDetails?.brand_id || "";

export const sha256Hex = async (data) =>
  Array.from(new Uint8Array(await crypto.subtle.digest("SHA-256", data)), (b) =>
    b.toString(16).padStart(2, "0")
  ).join("");

export const hashBlob = async (blob) => sha256Hex(await blob.arrayBuffer());

// ONE file per request: the backend reuses a single `data` object across
// req.files and overwrites data.urls per file, so a multi-file post returns
// only the last file's urls.
export const createEditorAsset = async (
  file,
  type,
  brandId,
  name = file.name,
  contentKey
) => {
  const fd = new FormData();
  fd.append(
    "data",
    JSON.stringify({
      type,
      // A super admin has no brand, and the model casts brand_id to an
      // ObjectId, so an empty string fails validation. Omit the key entirely
      // and the asset is created global, which is what an unscoped admin wants.
      ...(brandId ? { brand_id: brandId } : {}),
      ...(contentKey ? { content_key: contentKey } : {}),
      display_in_app: true,
      display_in_web: true,
    })
  );
  fd.append("files", file);

  const res = await apiMultiPartPost(ENDPOINTS.createEditorSetting, fd);
  if (!res || res.error || res.status === 0) {
    throw new Error(res?.error || `Failed to add asset "${name}"`);
  }
  const item = Array.isArray(res.items) ? res.items[0] : res.items;
  const picked = pickUrls(item?.urls);
  if (!picked.url) {
    throw new Error(
      `Asset "${name}" uploaded but the response carried no URL: ${JSON.stringify(res).slice(0, 200)}`
    );
  }
  // The asset's id becomes a sticker's sticker_id (sticker history keys on it).
  return { ...picked, assetType: type, assetId: item?._id || null };
};

// The library asset already registered under `contentKey`, or null. apiPost
// adds the admin's brand to the filter, so this sees that brand's assets and
// the global ones, which is exactly what a new upload would join.
// ⚠️ The match is CONFIRMED on the returned content_key: a server without the
// content_key filter ignores it and returns the newest asset of the type, which
// must never be mistaken for this image. A lookup that fails is treated as "not
// found", so the worst case is today's behaviour (one more upload).
export const findLibraryAsset = async (type, contentKey) => {
  if (!contentKey) return null;
  try {
    const res = await apiPost(ENDPOINTS.getStickers, {
      filter: { status: 1, type, content_key: contentKey },
      skip: 0,
      limit: 1,
    });
    const item = res?.items?.[0];
    if (!item || item.content_key !== contentKey) return null;
    const picked = pickUrls(item.urls);
    if (!picked.url) return null;
    return { ...picked, assetType: type, assetId: item._id || null, existing: true };
  } catch (err) {
    console.warn("[editorAssets] library lookup failed, uploading instead:", err);
    return null;
  }
};

// type:content_key -> asset, THIS SESSION only, so repeats inside one sitting
// skip even the lookup. Not persisted, for the same reasons as the importer's
// upload cache (psdImportService.js): a stale entry would point at an asset
// deleted since. The server lookup is the cross-session, cross-PC check.
const assetByKey = new Map();

// Reuse the library asset for this content if there is one, else upload it.
export const ensureLibraryAsset = async ({ file, type, brandId, contentKey, name }) => {
  const cacheKey = `${type}:${contentKey}`;
  const cached = contentKey && assetByKey.get(cacheKey);
  if (cached) return { ...cached, existing: true };
  const asset =
    (await findLibraryAsset(type, contentKey)) ||
    (await createEditorAsset(file, type, brandId, name, contentKey));
  if (contentKey) assetByKey.set(cacheKey, { ...asset, existing: undefined });
  return asset;
};

// Exposed for tests.
export const _resetLibraryCache = () => assetByKey.clear();

// Put the image at `url` into a library list (Stickers or Backgrounds). Image
// storage serves `Access-Control-Allow-Origin: *`, so the bytes can be fetched
// in-page; the key is a hash of those bytes, identical on every PC.
export const addImageToLibrary = async (url, type, brandId) => {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Could not download the image (${res.status})`);
  const blob = await res.blob();
  const base = decodeURIComponent(url.split("?")[0].split("/").pop() || type);
  return ensureLibraryAsset({
    file: new File([blob], base, { type: blob.type }),
    type,
    brandId,
    contentKey: `img1:${await hashBlob(blob)}`,
  });
};

export const addImageToStickers = (url, brandId) =>
  addImageToLibrary(url, EDITOR_ASSETS.STICKER, brandId);
